/**
 * <gw-reviews-carousel> — arrows + dots for the "Reviews (GW)" review row.
 *
 * WHY NOT `<carousel-element>` (Flickity), which this section used to use:
 * the client reported the row cut off after ~1.5 cards with no way to reach the
 * rest. It was drag-only by construction — `CarouselElement.init()` hardcodes
 * `prevNextButtons: false` (assets/theme.js) and the section passed
 * `page-dots="false"`, so the ONLY affordance was press-and-drag. Flickity also
 * transforms the track rather than scrolling it, so the browser has nothing to
 * scroll: no trackpad two-finger swipe, no shift+wheel, no scrollbar, no
 * keyboard. Turning the buttons back on would mean editing the shared element
 * and every section that upgrades through it.
 *
 * So the row is now a plain `overflow-x: auto` scroll-snap track and this element
 * only adds the controls on top. Two-finger swipe, shift+wheel, touch, and
 * keyboard all come free from the browser; the arrows and dots exist so the
 * affordance is VISIBLE, which was the actual complaint.
 *
 * Deliberately NOT bound: hover-to-scroll (mouse-only, fires on cursor drift)
 * and vertical-wheel-to-horizontal (hijacks the page scroll — the page appears
 * to stop dead whenever the cursor crosses this section).
 *
 * Paging is ABSOLUTE (scroll to `index * itemOffset`) rather than relative, so a
 * repeated event cannot compound into the wrong card. Geometry is measured off
 * the live cards, so `--gw-rev-card-basis` / `--gw-rev-card-gap` stay the single
 * source of truth for the breakpoints and nothing is duplicated here.
 */
class GwReviewsCarousel extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;

    this.track = this.querySelector('.gw-reviews__grid');
    this.prev = this.querySelector('.gw-reviews__arrow--prev');
    this.next = this.querySelector('.gw-reviews__arrow--next');
    this.dots = this.querySelector('.gw-reviews__dots');
    if (!this.track) return;

    this.bound = true;
    this.rtl = getComputedStyle(this).direction === 'rtl';

    this.buildDots();

    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));

    this.onScroll = this.throttle(() => this.update());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.throttle(() => this.update()));
      this.observer.observe(this.track);
    }

    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  /** rAF-coalesced callback. */
  throttle(fn) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn();
      });
    };
  }

  get behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  get cards() {
    return Array.from(this.track.children);
  }

  get scrollPos() {
    return Math.abs(this.track.scrollLeft);
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  get scrollable() {
    return this.maxScroll > 1;
  }

  /** Leading edge to leading edge: card width + gap. */
  get itemOffset() {
    const cards = this.cards;
    if (cards.length < 2) return cards[0]?.clientWidth || 0;
    return Math.abs(cards[1].offsetLeft - cards[0].offsetLeft);
  }

  /**
   * One full view, rounded to whole cards — so a click always lands the next
   * card flush against the gutter instead of parking it half-cut, which is the
   * state the client reported in the first place.
   */
  get pageSize() {
    const offset = this.itemOffset;
    if (!offset) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / offset));
    return perView * offset;
  }

  /** Which card sits at the leading edge, derived from scroll position. */
  get currentIndex() {
    const offset = this.itemOffset;
    if (!offset) return 0;
    return Math.min(this.cards.length - 1, Math.round(this.scrollPos / offset));
  }

  page(direction) {
    if (!this.scrollable) return;
    // scrollBy resolves the RTL sign itself.
    this.track.scrollBy({ left: direction * this.pageSize, behavior: this.behavior });
  }

  select(index) {
    const target = Math.max(0, Math.min(index * this.itemOffset, this.maxScroll));
    this.track.scrollTo({ left: target * (this.rtl ? -1 : 1), behavior: this.behavior });
  }

  buildDots() {
    if (!this.dots) return;

    this.cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'gw-reviews__dot';
      dot.setAttribute('aria-label', `${this.dots.dataset.label || 'Review'} ${index + 1}`);
      dot.addEventListener('click', () => this.select(index));
      this.dots.appendChild(dot);
    });

    this.dotButtons = Array.from(this.dots.children);
  }

  update() {
    const scrollable = this.scrollable;

    // With few enough reviews to fill the row there is nothing to page through,
    // and a permanently dead control reads as broken — drop the whole bar.
    this.classList.toggle('gw-reviews__carousel--static', !scrollable);

    if (this.prev) this.prev.disabled = this.scrollPos <= 1;
    if (this.next) this.next.disabled = this.scrollPos >= this.maxScroll - 1;

    const index = this.currentIndex;
    this.dotButtons?.forEach((dot, i) => {
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  }
}

if (!customElements.get('gw-reviews-carousel')) {
  customElements.define('gw-reviews-carousel', GwReviewsCarousel);
}
